import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';

/**
 * Die Teile, die JEDER gepflegte Abschnitt der Einstellungen gleich hat.
 *
 * WARUM ES DIESE DATEI GIBT — zweimal bezahlt, gemessen
 *   A43 (#120) hat „stillgelegt" aus dem abschneidenden Text geholt, weil
 *   die Kennzeichnung bei 390 Pixeln mit abgeschnitten wurde und sich
 *   „stillg…" las. Zwei Abschnitte wurden damals korrigiert, der dritte
 *   blieb als Befund liegen.
 *
 *   A54 (#146) musste GENAU DIESELBE Korrektur im Wege-Abschnitt noch
 *   einmal machen — dazu ein fehlendes `shrink-0` am Kästchen, das der
 *   Zwilling längst trug (13 statt 20 Pixel breit, gemessen).
 *
 *   Beim nächsten Mal wäre es das dritte Mal. Deshalb steht die Gestalt
 *   jetzt HIER, einmal, und die Abschnitte reichen nur noch das herein,
 *   worin sie sich wirklich unterscheiden.
 *
 * WAS HIER STEHT UND WAS NICHT
 *   Hier steht, was in beiden Abschnitten ZEICHENGLEICH war. Alles, was
 *   sich unterscheidet — der Knopf zum Hinzufügen, der Auswahl-Schalter,
 *   die Hilfesätze —, ist bewusst NICHT hergezogen: Es zusammenzulegen
 *   hiesse, eines von beiden anders aussehen zu lassen, und dieser Umbau
 *   ändert kein Verhalten. Der Bericht zu A59 führt die Stellen einzeln
 *   auf.
 *
 *   Die Regel dahinter: Gemeinsam wird, was gleich AUSSIEHT, nicht was
 *   gleich HEISST.
 */

/**
 * Der Rahmen eines Abschnitts: Überschrift, Herkunftssatz, Ladezeile,
 * Fehlerzeile — und darunter, was der Abschnitt selbst mitbringt.
 */
export function AbschnittRahmen({
  ankerId,
  titel,
  einleitung,
  laedt,
  fehler,
  children
}: {
  ankerId: string;
  titel: string;
  /** Ein Satz: woher die Einträge kommen und was hier geht. */
  einleitung: string;
  laedt: boolean;
  fehler: boolean;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={ankerId} className="mt-8 space-y-3">
      <div>
        <h2 id={ankerId} className="font-serif text-lg text-ink">
          {titel}
        </h2>
        <p className="mt-1 text-sm text-ink-soft">{einleitung}</p>
      </div>

      {laedt && (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <Spinner /> {titel} werden geladen …
        </p>
      )}
      {fehler && <p className="text-sm text-coral">Die Liste ließ sich nicht laden.</p>}

      {children}
    </section>
  );
}

/**
 * Die zusammenklappbare Kopfzeile eines Eintrags.
 *
 * DIE KENNZEICHNUNG STEHT NEBEN DEM ABSCHNEIDENDEN TEXT, NICHT DARIN.
 * Das ist der Griff, den A43 und A54 je einmal einbauen mussten:
 * Gemessen bei 390 Pixeln an einem langen Namen brauchte die
 * Beschriftung 496 Pixel und bekam 218; „(stillgelegt)" hatte danach
 * eine sichtbare Breite von 0. Im Quelltext stand es weiter da — eine
 * Zusicherung über `getByText` fängt diesen Fall deshalb nicht, und die
 * Zusicherung dazu prüft die Kette nach oben bis zur ersten
 * abschneidenden Kante (stillgelegtSichtbar.test.tsx).
 *
 * Nicht nur blass, sondern benannt: „stillgelegt" ist eine Auskunft, und
 * eine Farbe allein ist keine (E40).
 */
export function ZeilenKopf({
  offen,
  onUmschalten,
  vorne,
  label,
  aktiv,
  unterzeile
}: {
  offen: boolean;
  onUmschalten: () => void;
  /** Was links vor der Beschriftung steht — Sortiernummer oder Sinnbild. */
  vorne?: ReactNode;
  label: string;
  aktiv: boolean;
  /** Die zweite Zeile: Schlüssel, je nach Abschnitt mit Zusatz. */
  unterzeile: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onUmschalten}
      aria-expanded={offen}
      className="flex min-h-[44px] w-full items-center gap-3 px-3 py-2 text-left"
    >
      {vorne}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="min-w-0 truncate text-sm text-ink">{label}</span>
          {!aktiv && (
            <span className="shrink-0 rounded border border-line px-1.5 py-0.5 text-xs text-ink-soft">
              stillgelegt
            </span>
          )}
        </span>
        {unterzeile}
      </span>
      <span className="shrink-0 text-xs text-akzent">{offen ? 'Schließen' : 'Ändern'}</span>
    </button>
  );
}

/**
 * Ein Feld, das dasteht, aber nicht zu ändern ist — mit Schloss und
 * einem Satz, warum.
 *
 * Drei Verwendungen heute: der Schlüssel in beiden Abschnitten und das
 * Zusatzfeld `scope` bei den Checklisten-Punkten (A51). Der Grund steht
 * je Verwendung woanders, die Gestalt ist dieselbe.
 */
export function GesperrtesFeld({
  titel,
  wert,
  hilfe,
  mono = false
}: {
  titel: string;
  wert: ReactNode;
  hilfe: ReactNode;
  /** Schlüssel werden einsilbig geschrieben und stehen in Schreibmaschine. */
  mono?: boolean;
}) {
  return (
    <label className="block text-xs text-ink-soft">
      {titel}
      <span className="mt-1 flex items-center gap-2 rounded-md border border-line bg-cream-warm px-3 py-2 text-sm text-ink-soft">
        <Lock aria-hidden className="h-4 w-4 shrink-0" />
        <span className={mono ? 'min-w-0 flex-1 truncate font-mono' : 'min-w-0 flex-1 truncate'}>
          {wert}
        </span>
      </span>
      <span className="mt-1 block text-ink-faint">{hilfe}</span>
    </label>
  );
}
